import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ClientSideRowModelModule, ColDef, GetRowIdParams, ModuleRegistry } from "ag-grid-community";
import { ExcelExportModule, LicenseManager, SetFilterModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { DateFormat, formatDate } from "common";
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { CustomColumnHeader } from "@/components/Reports/CustomReports/CustomReportGrid";
import { LedgerRow, NULL_VALUE } from "@/components/Reports/MerchandiseLedger/merchandiseLedgerHelper";
import { config } from "@/config";

ModuleRegistry.registerModules([ClientSideRowModelModule, SetFilterModule, ExcelExportModule]);
if (config.agGrid?.key) {
    LicenseManager.setLicenseKey(config.agGrid.key);
}

// Imperative handle: parent triggers a client-side .xlsx export of the grid as shown (visible
// columns, current filters) via ag-grid's native exporter. Sorting is disabled on this grid.
export interface LedgerGridHandle {
    exportToExcel: (fileName: string) => void;
}

interface LedgerGridProps {
    rows: LedgerRow[];
    userNames: Record<string, string>;
    userSources: Record<string, string>;
    productNames: Record<string, string>;
    variantNames: Record<string, string>;
    // Variant / Event Category columns; hidden for product changes.
    showInventoryColumns?: boolean;
    // Quantity / Reason / Comment columns; only on the "all" and "inventory" tabs.
    showChangeDetailColumns?: boolean;
    isLoading?: boolean;
    // A failed audit query: show an error overlay instead of the "no changes" empty state, so partial or
    // dropped data isn't mistaken for a complete, empty ledger.
    isError?: boolean;
    emptyMessage?: string;
}

const defaultColDef: ColDef = {
    flex: 1,
    menuTabs: [],
    filter: false,
    minWidth: 140,
    resizable: true,
    sortable: false,
    // Values are pre-formatted strings; disable ag-grid type inference so a numeric Quantity column
    // doesn't render the NULL_VALUE placeholder rows as "Invalid Number".
    cellDataType: false,
};

const components = { agColumnHeader: CustomColumnHeader };

const LEDGER_ERROR_MESSAGE = "Couldn't load the ledger. Refresh to try again.";

// Rendered as a React child (not ag-grid's innerHTML `overlayNoRowsTemplate`), so an interpolated
// message can never inject markup.
const NoRowsOverlay = ({ message }: { message: string }) => <span className="text-gray-dark">{message}</span>;

// Set-filter columns (User, Variant, Event Category, Event Type, Source) get the filter header via the shared
// CustomColumnHeader, the other columns are display-only.
const setFilter: ColDef = { filter: "agSetColumnFilter" };

const getRowId = ({ data }: GetRowIdParams) => data.rowId;

export const LedgerGrid = forwardRef<LedgerGridHandle, LedgerGridProps>(function LedgerGrid(props, ref) {
    const { rows, userNames, userSources, productNames, variantNames } = props;
    const { showInventoryColumns = false, showChangeDetailColumns = false } = props;
    const { isLoading = false, isError = false, emptyMessage = "No changes in this period." } = props;

    const gridRef = useRef<AgGridReact>(null);
    useImperativeHandle(
        ref,
        () => {
            return { exportToExcel: (fileName: string) => gridRef.current?.api?.exportDataAsExcel({ fileName }) };
        },
        [],
    );

    // Portal the set-filter popups to <body> so the header can't clip them. Must be a node React
    // doesn't manage: portaling into the report layout let a re-render clobber an open popup and leave
    // ag-grid's "filter shown" state stuck, so the menu wouldn't reopen a second time.
    const popupParent = useMemo<HTMLElement>(() => document.body, []);

    const rowData = useMemo(() => {
        return rows.map((row, index) => {
            return {
                // One audit can emit several rows, so key on the audit id plus its position.
                rowId: `${row.auditId}-${index}`,
                date: formatDate(row.date, DateFormat.DATE_TIME),
                auditId: row.auditId,
                user: row.userId ? userNames[row.userId] ?? NULL_VALUE : NULL_VALUE,
                product: productNames[row.objectId] ?? NULL_VALUE,
                variant: variantNames[row.objectId] ?? NULL_VALUE,
                category: row.category ?? NULL_VALUE,
                eventType: row.changeLabel,
                from: row.from,
                to: row.to,
                quantity: row.quantity ?? NULL_VALUE,
                reason: row.reason ?? NULL_VALUE,
                comment: row.comment ?? NULL_VALUE,
                source: row.userId ? userSources[row.userId] ?? NULL_VALUE : NULL_VALUE,
            };
        });
    }, [rows, userNames, userSources, productNames, variantNames]);

    const columnDefs = useMemo<ColDef[]>(() => {
        const columns = [
            { field: "date", headerName: "Date", minWidth: 170 },
            { field: "auditId", headerName: "Audit Id", minWidth: 200 },
            { field: "user", headerName: "User", ...setFilter },
            { field: "product", headerName: "Product" },
            showInventoryColumns && { field: "variant", headerName: "Variant", ...setFilter },
            showInventoryColumns && { field: "category", headerName: "Event Category", ...setFilter },
            { field: "eventType", headerName: "Event Type", ...setFilter },
            { field: "from", headerName: "From State" },
            { field: "to", headerName: "To State" },
            showChangeDetailColumns && { field: "quantity", headerName: "Quantity", minWidth: 110 },
            showChangeDetailColumns && { field: "reason", headerName: "Reason" },
            showChangeDetailColumns && { field: "comment", headerName: "Comment" },
            { field: "source", headerName: "Source", minWidth: 120, ...setFilter },
        ];

        return columns.filter(Boolean) as ColDef[];
    }, [showInventoryColumns, showChangeDetailColumns]);

    const noRowsOverlayParams = { message: isError ? LEDGER_ERROR_MESSAGE : emptyMessage };

    return (
        <div className="ag-theme-alpine h-full w-full">
            <AgGridReact
                ref={gridRef}
                suppressContextMenu
                suppressCellFocus
                theme="legacy"
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                components={components}
                getRowId={getRowId}
                popupParent={popupParent}
                loading={isLoading}
                noRowsOverlayComponent={NoRowsOverlay}
                noRowsOverlayComponentParams={noRowsOverlayParams}
            />
        </div>
    );
});
